import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Typography } from '../theme';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { callSendVerificationEmail } from '../utils/studentVerification';

// "Enter your university email" dialog — the same send path as the
// Verification Center / Profile student-verification panel
// (callSendVerificationEmail), usable from anywhere (StudiZone's locked
// screen opens it from "Verify Student Status").

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Prefill: the profile's student / requested student / account email. */
  initialEmail?: string;
  /** A verification email was already sent and is waiting to be confirmed. */
  pending?: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function StudentEmailVerifyModal({ visible, onClose, initialEmail = '', pending = false }: Props) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef<View>(null);
  const lift = useRef(new Animated.Value(0)).current;
  const liftValue = useRef(0);

  // Android: keep the card (email field + buttons) above the keyboard. Under
  // edge-to-edge the dialog window may or may not resize for the keyboard,
  // so measure where the card actually is and lift it by exactly the overlap
  // (0 if the window already made room). iOS uses KeyboardAvoidingView below.
  useEffect(() => {
    if (!visible || Platform.OS !== 'android') return undefined;
    const animateTo = (value: number) => {
      liftValue.current = value;
      Animated.timing(lift, { toValue: -value, duration: 180, useNativeDriver: true }).start();
    };
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onShow = (event: { endCoordinates: { screenY: number } }) => {
      const keyboardTop = event.endCoordinates.screenY;
      if (timer) clearTimeout(timer);
      // Let a resizing window settle first, then measure.
      timer = setTimeout(() => {
        cardRef.current?.measureInWindow((_x, y, _w, height) => {
          const top = y + liftValue.current;
          const bottom = top + height;
          const overlap = Math.max(0, bottom + 16 - keyboardTop);
          animateTo(Math.min(overlap, Math.max(0, top - 24)));
        });
      }, 60);
    };
    const show = Keyboard.addListener('keyboardDidShow', onShow);
    const hide = Keyboard.addListener('keyboardDidHide', () => animateTo(0));
    return () => {
      if (timer) clearTimeout(timer);
      show.remove();
      hide.remove();
      animateTo(0);
    };
  }, [lift, visible]);

  useEffect(() => {
    if (visible) {
      setEmail(initialEmail);
      setSending(false);
      setSent(false);
      setError('');
    }
  }, [visible, initialEmail]);

  const send = async () => {
    const next = email.trim().toLowerCase();
    if (!EMAIL_RE.test(next)) {
      setError(t('verification.validStudentEmail'));
      return;
    }
    setSending(true);
    setError('');
    try {
      await callSendVerificationEmail(next);
      setSent(true);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('verification.emailSendFailed'));
    } finally {
      setSending(false);
    }
  };

  const dark = theme.isDark;
  const card = { backgroundColor: dark ? 'rgba(10,14,24,0.98)' : theme.colors.cardBackground, borderColor: dark ? 'rgba(38,208,206,0.22)' : theme.colors.borderStrong };
  const textPrimary = { color: theme.colors.textPrimary };
  const textSecondary = { color: theme.colors.textSecondary };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel={t('common.cancel')} />
        <Animated.View ref={cardRef} style={[styles.card, card, { transform: [{ translateY: lift }] }]} accessibilityViewIsModal>
          <View style={styles.iconWrap}>
            <Icon name={sent ? 'mail' : 'award'} size={22} color={Colors.turquoise} />
          </View>
          <Text style={[styles.title, textPrimary]} accessibilityRole="header">
            {sent ? t('profile.verificationEmailSentTitle') : t('settings.studentVerification')}
          </Text>
          <Text style={[styles.body, textSecondary]}>
            {sent ? t('verification.emailSent') : pending ? t('profile.verificationCheckInboxBody') : t('settings.universityEmailHelp')}
          </Text>
          {!sent && (
            <TextInput
              value={email}
              onChangeText={value => {
                setEmail(value);
                if (error) setError('');
              }}
              placeholder={t('verification.emailPlaceholder')}
              placeholderTextColor={theme.colors.textMuted}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              keyboardType="email-address"
              textContentType="emailAddress"
              returnKeyType="send"
              onSubmitEditing={send}
              editable={!sending}
              style={[styles.input, { color: theme.colors.textPrimary, borderColor: theme.colors.border, backgroundColor: dark ? 'rgba(255,255,255,0.06)' : theme.colors.surface }]}
              accessibilityLabel={t('settings.universityEmail')}
            />
          )}
          {error ? <Text style={styles.error} accessibilityLiveRegion="polite">{error}</Text> : null}
          <View style={styles.buttons}>
            {sent ? (
              <TouchableOpacity style={[styles.button, styles.primary]} onPress={onClose} accessibilityRole="button">
                <Text style={styles.primaryText}>{t('vibeClaim.ok')}</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity style={[styles.button, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : theme.colors.surfaceElevated }]} onPress={onClose} accessibilityRole="button">
                  <Text style={[styles.secondaryText, textSecondary]}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, styles.primary, sending && styles.disabled]}
                  onPress={send}
                  disabled={sending}
                  accessibilityRole="button"
                  accessibilityState={{ busy: sending, disabled: sending }}
                >
                  {sending
                    ? <ActivityIndicator color="#fff" />
                    : <Text style={styles.primaryText}>{pending ? t('settings.resendVerification') : t('studiZone.verifyStatus')}</Text>}
                </TouchableOpacity>
              </>
            )}
          </View>
        </Animated.View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 22,
    backgroundColor: 'rgba(2,4,10,0.6)',
  },
  card: {
    borderRadius: 24,
    borderWidth: 1,
    padding: 22,
    gap: 12,
    alignItems: 'center',
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,208,206,0.12)',
  },
  title: {
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  body: {
    fontSize: Typography.base,
    lineHeight: 21,
    textAlign: 'center',
  },
  input: {
    alignSelf: 'stretch',
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1,
    paddingHorizontal: 14,
    fontSize: 16,
  },
  error: {
    color: '#DC2626',
    fontSize: Typography.sm,
    lineHeight: 19,
    textAlign: 'center',
  },
  buttons: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 10,
    marginTop: 4,
  },
  button: {
    flex: 1,
    minHeight: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  primary: { backgroundColor: Colors.sunset },
  disabled: { opacity: 0.6 },
  primaryText: { color: '#fff', fontSize: Typography.base, fontWeight: Typography.bold, textAlign: 'center' },
  secondaryText: { fontSize: Typography.base, fontWeight: Typography.semibold },
});
